import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import DialogChatAPI from "../../../api/dialogChatApi";
import UsersApi from "../../../api/usersApi";
import { TextChatCapabilities } from "../../../components/TextChat/TextChat.types";
import { TextChatMessageAction } from "../../../components/TextChat/TextChatMessage/TextChatMessage.types";
import {
  DialogChatSocketAdapter,
  DialogNewMessageEvent,
} from "../../../features/dialogChat/dialogChatSocketAdapter";
import {
  DialogChatVoiceDraft,
  refreshDialogChatVoicePlaybackUrl,
  uploadAndSendDialogChatVoiceMessage,
} from "../../../features/dialogChat/dialogChatVoiceMessages";
import strings from "../../../strings";
import {
  DialogChat,
  DialogChatFile,
  PinnedDialogMessage,
  TypeDialogMessage,
} from "../../../types/DialogChat";
import { TypeUserData } from "../../../types/TypeUsers";
import { getTranslation } from "../../../utils/getTranslation";
import {
  findDialogChatForIntent,
  getGlobalChatIntentKey,
  GlobalChatRouteState,
  resolveGlobalChatIntent,
} from "../globalChatIntent";
import {
  createInitialGlobalChatCacheState,
  globalChatCacheReducer,
  GlobalChatMessage,
  selectConversations,
  selectMessagesForChat,
} from "./globalChatCache";
import {
  canReplyToGlobalChatMessage,
  canSendStructuredTextMessage,
  createReplyPreviewFromDialogMessage,
  createReplyPreviewFromMessage,
  dedupeMentionIdsForChat,
  getGlobalChatMentionCandidates,
  GlobalChatMentionCandidate,
  insertMentionText,
} from "./globalChatComposition";
import {
  groupGlobalChatConversations,
  setUserIdInList,
} from "./globalChatConversationActions";
import {
  getGlobalChatMessageActionAvailability,
  getVisibleMessageText,
} from "./globalChatMessageActions";
import {
  getExplicitlyReadableMessageIds,
  isDialogChatMutedForUser,
} from "./globalChatReadState";
import {
  getTypingIndicatorText,
  GLOBAL_CHAT_REMOTE_TYPING_TIMEOUT_MS,
  GLOBAL_CHAT_TYPING_IDLE_TIMEOUT_MS,
  shouldSendTypingStart,
} from "./globalChatTypingState";

const CONVERSATIONS_PAGE_LIMIT = 20;
const MESSAGES_PAGE_LIMIT = 50;
const MESSAGE_SEARCH_LIMIT = 20;
const JUMP_HIGHLIGHT_DURATION_MS = 1800;

export type GlobalChatPinnedMessageItem = {
  messageId: string;
  pinnedAt: string;
  previewText: string;
};

type UseGlobalChatControllerParams = {
  token: string | null;
  currentUser?: TypeUserData | null;
  routeState?: GlobalChatRouteState | null;
  searchParams: URLSearchParams;
  directUserId?: string | null;
  onDirectUserIdHandled?: () => void;
  socketAdapter: DialogChatSocketAdapter;
  unreadMessagesCount: number;
  onUnreadMessagesCountChange: (count: number) => void;
  capabilities?: Pick<TextChatCapabilities, "reactions">;
};

const normalizeMessage = (
  message: TypeDialogMessage,
  currentUserId: string,
  selectedLanguage?: string,
): GlobalChatMessage => {
  const label = message.labels?.find(
    (item) => item.language === selectedLanguage,
  );

  return {
    messageId: message._id,
    user: message.user,
    text: label?.text ?? message.message ?? "",
    sender:
      message.user !== "System" && message.user._id === currentUserId
        ? "user"
        : "other",
    files: message.files || [],
    voice: message.voice,
    date: new Date(message.date),
    realTimeBooking: message.realTimeBooking,
    reactions: message.reactions
      ? (message.reactions as GlobalChatMessage["reactions"])
      : [],
    readBy: message.readBy || [],
    mentions: message.mentions || [],
    replyTo:
      typeof message.replyTo === "string"
        ? message.replyTo
        : message.replyTo?._id,
    replyToMessage: createReplyPreviewFromDialogMessage(
      message.replyTo,
      selectedLanguage,
    ),
    deletedAt: message.deletedAt,
    deletedBy: message.deletedBy,
    editedAt: message.editedAt,
    editedBy: message.editedBy,
  };
};

const normalizeSocketMessage = (
  event: DialogNewMessageEvent,
  currentUserId: string,
  selectedLanguage?: string,
) => normalizeMessage(event.newMessage, currentUserId, selectedLanguage);

const getPinnedMessageId = (pin: PinnedDialogMessage) =>
  typeof pin.message === "string" ? pin.message : pin.message._id;

const createPinnedMessagePreview = (
  pin: PinnedDialogMessage,
  messagesById: Map<string, GlobalChatMessage>,
  currentUserId?: string,
  selectedLanguage?: string,
): GlobalChatPinnedMessageItem => {
  const messageId = getPinnedMessageId(pin);
  const cachedMessage = messagesById.get(messageId);
  const populatedMessage =
    typeof pin.message === "string" ? undefined : pin.message;
  const normalizedPopulatedMessage =
    populatedMessage && currentUserId
      ? normalizeMessage(populatedMessage, currentUserId, selectedLanguage)
      : undefined;
  const message = cachedMessage || normalizedPopulatedMessage;

  return {
    messageId,
    pinnedAt: pin.pinnedAt,
    previewText:
      message?.text ||
      (message?.files.length
        ? strings.globalChatAttachmentPreview
        : message?.voice
          ? strings.globalChatVoiceMessagePreview
          : strings.globalChatPinnedMessagePreview),
  };
};

const getLocalFileType = (file: File) => {
  const subtype = file.type.split("/")[1];
  if (subtype) {
    return subtype;
  }

  return file.name.split(".").pop() || "file";
};

const formatLocalFileSize = (size: number) => {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }

  return `${Math.round((size / 1024 / 1024) * 10) / 10} MB`;
};

export const useGlobalChatController = ({
  token,
  currentUser,
  routeState,
  searchParams,
  directUserId,
  onDirectUserIdHandled,
  socketAdapter,
  unreadMessagesCount,
  onUnreadMessagesCountChange,
  capabilities = { reactions: true },
}: UseGlobalChatControllerParams) => {
  const [state, dispatch] = useReducer(
    globalChatCacheReducer,
    undefined,
    createInitialGlobalChatCacheState,
  );
  const [composerText, setComposerText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedMentionIds, setSelectedMentionIds] = useState<string[]>([]);
  const [replyTarget, setReplyTarget] = useState<GlobalChatMessage | null>(
    null,
  );
  const [editingMessage, setEditingMessage] =
    useState<GlobalChatMessage | null>(null);
  const [pendingDeleteMessage, setPendingDeleteMessage] =
    useState<GlobalChatMessage | null>(null);
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);
  const [messageSearchQuery, setMessageSearchQuery] = useState("");
  const [messageSearchResults, setMessageSearchResults] = useState<
    GlobalChatMessage[]
  >([]);
  const [isMessageSearchLoading, setIsMessageSearchLoading] = useState(false);
  const [isJumpingToSearchResult, setIsJumpingToSearchResult] = useState(false);
  const [isJumpingToPinnedMessage, setIsJumpingToPinnedMessage] =
    useState(false);
  const [messageSearchError, setMessageSearchError] = useState("");
  const [pinnedMessageError, setPinnedMessageError] = useState("");
  const [focusTargetMessageId, setFocusTargetMessageId] = useState<
    string | null
  >(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const [typingUserIdsByChatId, setTypingUserIdsByChatId] = useState<
    Record<string, string[]>
  >({});
  const [handledRouteIntentKey, setHandledRouteIntentKey] = useState<
    string | null
  >(null);
  const messageRequestIdRef = useRef(0);
  const messageSearchRequestIdRef = useRef(0);
  const localMessageIdRef = useRef(0);
  const readMarkInFlightRef = useRef(new Set<string>());
  const messagePageRequestsInFlightRef = useRef(new Set<string>());
  const localFilePreviewUrlsRef = useRef(new Map<string, string[]>());
  const localVoicePreviewUrlsRef = useRef(new Map<string, string>());
  const tokenRef = useRef(token);
  const selectedChatIdRef = useRef<string | null>(null);
  const isLocalTypingRef = useRef(false);
  const isComposerFocusedRef = useRef(false);
  const localTypingLastStartedAtRef = useRef(0);
  const localTypingIdleTimeoutRef = useRef<number | null>(null);
  const remoteTypingTimeoutsRef = useRef(new Map<string, number>());

  const currentUserId = currentUser?._id;
  const selectedLanguage = currentUser?.selectedLanguage;
  const conversations = useMemo(() => selectConversations(state), [state]);
  const groupedConversations = useMemo(
    () => groupGlobalChatConversations(conversations, currentUserId),
    [conversations, currentUserId],
  );
  const selectedChat = state.selectedChatId
    ? (state.conversationsById[state.selectedChatId] ?? null)
    : null;
  const selectedMessages = useMemo(
    () => selectMessagesForChat(state, state.selectedChatId),
    [state],
  );
  const selectedMessagesEntry = state.selectedChatId
    ? state.messagesByChatId[state.selectedChatId]
    : undefined;
  const selectedMessagesHasMore = selectedMessagesEntry?.hasMore ?? true;
  const selectedMessagesHasNewer = selectedMessagesEntry?.hasNewer ?? false;
  const selectedMessagesLoading = selectedMessagesEntry?.isLoading ?? false;
  const selectedMessagesById = useMemo(
    () =>
      new Map(selectedMessages.map((message) => [message.messageId, message])),
    [selectedMessages],
  );
  const mentionCandidates = useMemo(
    () =>
      getGlobalChatMentionCandidates(
        selectedChat,
        currentUserId,
        selectedLanguage,
      ),
    [currentUserId, selectedChat, selectedLanguage],
  );
  const selectedMentionCandidates = useMemo(
    () =>
      mentionCandidates.filter((candidate) =>
        selectedMentionIds.includes(candidate.id),
      ),
    [mentionCandidates, selectedMentionIds],
  );
  const canSendComposerMessage = useMemo(() => {
    if (editingMessage) {
      const nextText = composerText.trim();
      return (
        nextText.length > 0 && nextText !== (editingMessage.text || "").trim()
      );
    }

    return canSendStructuredTextMessage({
      text: composerText,
      filesCount: selectedFiles.length,
      selectedMentions: selectedMentionCandidates,
    });
  }, [
    composerText,
    editingMessage,
    selectedFiles.length,
    selectedMentionCandidates,
  ]);
  const selectedPinnedMessages = useMemo(
    () =>
      (selectedChat?.pinnedMessages || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.pinnedAt).getTime() - new Date(a.pinnedAt).getTime(),
        )
        .map((pin) =>
          createPinnedMessagePreview(
            pin,
            selectedMessagesById,
            currentUserId,
            selectedLanguage,
          ),
        ),
    [
      currentUserId,
      selectedChat?.pinnedMessages,
      selectedLanguage,
      selectedMessagesById,
    ],
  );
  const selectedChatBlockedBy = selectedChat?.blockedBy || [];
  const isSelectedChatBlocked = selectedChatBlockedBy.length > 0;
  const isSelectedChatExpired = selectedChat?.activeTo
    ? new Date(selectedChat.activeTo) < new Date()
    : false;
  const canWriteInSelectedChat =
    Boolean(selectedChat) && !isSelectedChatBlocked && !isSelectedChatExpired;
  const selectedTypingIndicatorText = useMemo(
    () =>
      getTypingIndicatorText({
        chat: selectedChat,
        typingUserIds: selectedChat?._id
          ? typingUserIdsByChatId[selectedChat._id]
          : [],
        currentUserId,
        selectedLanguage,
      }),
    [currentUserId, selectedChat, selectedLanguage, typingUserIdsByChatId],
  );

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    selectedChatIdRef.current = selectedChat?._id ?? null;
  }, [selectedChat?._id]);

  const clearLocalTypingIdleTimeout = useCallback(() => {
    if (localTypingIdleTimeoutRef.current !== null) {
      window.clearTimeout(localTypingIdleTimeoutRef.current);
      localTypingIdleTimeoutRef.current = null;
    }
  }, []);

  const stopLocalTyping = useCallback(
    (chatId = selectedChatIdRef.current) => {
      clearLocalTypingIdleTimeout();

      if (!isLocalTypingRef.current) {
        return;
      }

      isLocalTypingRef.current = false;
      localTypingLastStartedAtRef.current = 0;

      const activeToken = tokenRef.current;
      if (activeToken && chatId) {
        void DialogChatAPI.stopTypingInDialogChat(activeToken, chatId);
      }
    },
    [clearLocalTypingIdleTimeout],
  );

  const scheduleLocalTypingIdleStop = useCallback(() => {
    clearLocalTypingIdleTimeout();
    localTypingIdleTimeoutRef.current = window.setTimeout(() => {
      stopLocalTyping();
    }, GLOBAL_CHAT_TYPING_IDLE_TIMEOUT_MS);
  }, [clearLocalTypingIdleTimeout, stopLocalTyping]);

  const startLocalTyping = useCallback(
    (text: string) => {
      const activeToken = tokenRef.current;
      const chatId = selectedChatIdRef.current;
      const now = Date.now();

      if (
        !activeToken ||
        !chatId ||
        !shouldSendTypingStart({
          text,
          isFocused: isComposerFocusedRef.current,
          canWrite: canWriteInSelectedChat,
          isTyping: isLocalTypingRef.current,
          now,
          lastStartedAt: localTypingLastStartedAtRef.current,
        })
      ) {
        if (text.trim() && isLocalTypingRef.current) {
          scheduleLocalTypingIdleStop();
        }
        return;
      }

      isLocalTypingRef.current = true;
      localTypingLastStartedAtRef.current = now;
      void DialogChatAPI.startTypingInDialogChat(activeToken, chatId);
      scheduleLocalTypingIdleStop();
    },
    [canWriteInSelectedChat, scheduleLocalTypingIdleStop],
  );

  const handleComposerTextChange = useCallback(
    (text: string) => {
      setComposerText(text);

      if (editingMessage) {
        stopLocalTyping();
        return;
      }

      if (!text.trim()) {
        stopLocalTyping();
        return;
      }

      startLocalTyping(text);
    },
    [editingMessage, startLocalTyping, stopLocalTyping],
  );

  const handleComposerFocus = useCallback(() => {
    isComposerFocusedRef.current = true;
    if (editingMessage) {
      return;
    }
    startLocalTyping(composerText);
  }, [composerText, editingMessage, startLocalTyping]);

  const handleComposerBlur = useCallback(() => {
    isComposerFocusedRef.current = false;
    stopLocalTyping();
  }, [stopLocalTyping]);

  const revokeLocalFilePreviewUrls = useCallback((messageId: string) => {
    const urls = localFilePreviewUrlsRef.current.get(messageId) || [];
    urls.forEach((source) => URL.revokeObjectURL(source));
    localFilePreviewUrlsRef.current.delete(messageId);
  }, []);

  const createOptimisticMessage = useCallback(
    ({
      chatId,
      text,
      files,
      deliveryState,
      mentions = [],
      replyTo,
      replyToMessage,
    }: {
      chatId: string;
      text: string;
      files: File[];
      deliveryState: NonNullable<GlobalChatMessage["deliveryState"]>;
      mentions?: string[];
      replyTo?: string;
      replyToMessage?: GlobalChatMessage["replyToMessage"];
    }) => {
      const messageId = `local-${chatId}-${Date.now()}-${++localMessageIdRef.current}`;
      const previewUrls: string[] = [];
      const localFiles: DialogChatFile[] = files.map((file, index) => {
        const source = URL.createObjectURL(file);
        previewUrls.push(source);

        return {
          _id: `${messageId}-file-${index}`,
          type: getLocalFileType(file),
          filename: file.name,
          size: file.size,
          link: source,
        };
      });

      if (previewUrls.length > 0) {
        localFilePreviewUrlsRef.current.set(messageId, previewUrls);
      }

      return {
        messageId,
        user: currentUser
          ? {
              _id: currentUser._id,
              name: currentUser.name || [],
              surname: currentUser.surname || [],
              avatar: currentUser.avatarS3 || currentUser.avatar,
            }
          : undefined,
        text,
        sender: "user",
        files: localFiles.map((file) => ({
          ...file,
          size:
            file.size !== undefined
              ? formatLocalFileSize(file.size)
              : undefined,
        })),
        date: new Date(),
        reactions: [],
        readBy: [],
        mentions,
        replyTo,
        replyToMessage,
        deliveryState,
        isLocalOnly: true,
        retryPayload: {
          kind: "text",
          text,
          files,
          mentions,
          replyTo,
          replyToMessage,
        },
      } satisfies GlobalChatMessage;
    },
    [currentUser],
  );

  const createOptimisticVoiceMessage = useCallback(
    ({
      chatId,
      draft,
      deliveryState,
    }: {
      chatId: string;
      draft: DialogChatVoiceDraft;
      deliveryState: NonNullable<GlobalChatMessage["deliveryState"]>;
    }) => {
      const messageId = `local-voice-${chatId}-${Date.now()}-${++localMessageIdRef.current}`;
      localVoicePreviewUrlsRef.current.set(messageId, draft.url);

      return {
        messageId,
        user: currentUser
          ? {
              _id: currentUser._id,
              name: currentUser.name || [],
              surname: currentUser.surname || [],
              avatar: currentUser.avatarS3 || currentUser.avatar,
            }
          : undefined,
        text: "",
        sender: "user",
        files: [],
        voice: {
          filepath: draft.id,
          originalName: draft.file.name,
          mimeType: draft.file.type,
          size: draft.file.size,
          durationSeconds: draft.durationSeconds,
          url: draft.url,
        },
        date: new Date(),
        reactions: [],
        readBy: [],
        deliveryState,
        isLocalOnly: true,
        retryPayload: {
          kind: "voice",
          text: "",
          files: [],
          voiceDraft: {
            file: draft.file,
            url: draft.url,
            durationSeconds: draft.durationSeconds,
          },
        },
      } satisfies GlobalChatMessage;
    },
    [currentUser],
  );

  const sendLocalMessage = useCallback(
    async ({
      chat,
      localMessageId,
      text,
      files,
      mentions,
      replyTo,
    }: {
      chat: DialogChat;
      localMessageId: string;
      text: string;
      files: File[];
      mentions: string[];
      replyTo?: string;
    }) => {
      if (!currentUserId) {
        dispatch({
          type: "updateLocalMessageDeliveryState",
          chatId: chat._id,
          messageId: localMessageId,
          deliveryState: "failed",
        });
        return;
      }

      try {
        const response = await DialogChatAPI.sendMessageToDialogChat({
          dialogChatId: chat._id,
          message: text,
          userId: currentUserId,
          date: new Date(),
          files: Array.from(files).map((file) => ({
            name: file.name,
            buffer: file,
            size: file.size,
          })),
          mentions,
          replyTo,
        });

        if (response.status && response.newMessage) {
          revokeLocalFilePreviewUrls(localMessageId);
          dispatch({
            type: "replaceLocalMessage",
            chatId: chat._id,
            localMessageId,
            message: {
              ...normalizeMessage(
                response.newMessage,
                currentUserId,
                selectedLanguage,
              ),
              deliveryState: "sent",
              isLocalOnly: false,
            },
          });
          return;
        }
      } catch {
        // The failed local message remains in the mounted cache for retry/discard.
      }

      dispatch({
        type: "updateLocalMessageDeliveryState",
        chatId: chat._id,
        messageId: localMessageId,
        deliveryState: "failed",
      });
    },
    [currentUserId, revokeLocalFilePreviewUrls, selectedLanguage],
  );

  const revokeLocalVoicePreviewUrl = useCallback((messageId: string) => {
    const source = localVoicePreviewUrlsRef.current.get(messageId);
    if (source) {
      URL.revokeObjectURL(source);
      localVoicePreviewUrlsRef.current.delete(messageId);
    }
  }, []);

  const sendLocalVoiceMessage = useCallback(
    async ({
      chat,
      localMessageId,
      draft,
    }: {
      chat: DialogChat;
      localMessageId: string;
      draft: DialogChatVoiceDraft;
    }) => {
      if (!token || !currentUserId) {
        dispatch({
          type: "updateLocalMessageDeliveryState",
          chatId: chat._id,
          messageId: localMessageId,
          deliveryState: "failed",
        });
        return;
      }

      dispatch({
        type: "updateLocalMessageDeliveryState",
        chatId: chat._id,
        messageId: localMessageId,
        deliveryState: "queued",
      });

      try {
        const newMessage = await uploadAndSendDialogChatVoiceMessage({
          api: DialogChatAPI,
          token,
          userId: currentUserId,
          chatId: chat._id,
          draft,
        });

        revokeLocalVoicePreviewUrl(localMessageId);

        dispatch({
          type: "replaceLocalMessage",
          chatId: chat._id,
          localMessageId,
          message: {
            ...normalizeMessage(newMessage, currentUserId, selectedLanguage),
            deliveryState: "sent",
            isLocalOnly: false,
          },
        });

        return;
      } catch {
        dispatch({
          type: "updateLocalMessageDeliveryState",
          chatId: chat._id,
          messageId: localMessageId,
          deliveryState: "failed",
        });
      }
    },
    [currentUserId, revokeLocalVoicePreviewUrl, selectedLanguage, token],
  );

  const loadConversations = useCallback(
    async (page: number) => {
      if (!token || !currentUserId) {
        return;
      }

      dispatch({ type: "loadConversationsStart" });

      try {
        const response = await UsersApi.getUsersDialogChats(
          token,
          currentUserId,
          CONVERSATIONS_PAGE_LIMIT,
          page,
          state.conversationsSearch,
        );

        if (response.status) {
          dispatch({
            type: "loadConversationsSuccess",
            conversations: response.chats,
            page,
            hasMore: response.chats.length === CONVERSATIONS_PAGE_LIMIT,
          });
        } else {
          dispatch({ type: "loadConversationsError" });
        }
      } catch {
        dispatch({ type: "loadConversationsError" });
      }
    },
    [currentUserId, state.conversationsSearch, token],
  );

  const selectChat = useCallback(
    (chat: DialogChat | null) => {
      stopLocalTyping();
      setEditingMessage(null);
      setPendingDeleteMessage(null);
      setFocusTargetMessageId(null);
      setHighlightedMessageId(null);
      setIsJumpingToSearchResult(false);
      setIsJumpingToPinnedMessage(false);
      dispatch({
        type: "selectConversation",
        chatId: chat?._id ?? null,
      });
    },
    [stopLocalTyping],
  );

  const loadMessages = useCallback(
    async (chatId: string, page: number) => {
      if (!token || !currentUserId) {
        return false;
      }

      const entry = state.messagesByChatId[chatId];
      if (entry?.isLoading) {
        return false;
      }

      const requestKey = `${chatId}:${page}`;
      if (messagePageRequestsInFlightRef.current.has(requestKey)) {
        return false;
      }

      const isLoadingOlderRange = entry ? page > entry.page : page > 1;
      const isLoadingNewerRange = entry ? page < entry.newestPage : false;

      if (
        (isLoadingOlderRange && !entry?.hasMore) ||
        (isLoadingNewerRange && !entry?.hasNewer)
      ) {
        return false;
      }

      const requestId = ++messageRequestIdRef.current;
      messagePageRequestsInFlightRef.current.add(requestKey);
      dispatch({
        type: "loadMessagesStart",
        chatId,
        page,
        requestId,
      });

      try {
        const response = await DialogChatAPI.getDialogMessagesById(
          token,
          chatId,
          MESSAGES_PAGE_LIMIT,
          page,
        );

        if (response.status) {
          dispatch({
            type: "loadMessagesSuccess",
            chatId,
            page,
            requestId,
            messages: response.messages
              .map((message) =>
                normalizeMessage(message, currentUserId, selectedLanguage),
              )
              .reverse(),
            hasMore: response.messages.length === MESSAGES_PAGE_LIMIT,
          });
          return true;
        } else {
          dispatch({
            type: "loadMessagesError",
            chatId,
            page,
            requestId,
          });
          return false;
        }
      } catch {
        dispatch({
          type: "loadMessagesError",
          chatId,
          page,
          requestId,
        });
        return false;
      } finally {
        messagePageRequestsInFlightRef.current.delete(requestKey);
      }
    },
    [currentUserId, selectedLanguage, state.messagesByChatId, token],
  );

  const loadOlderMessages = useCallback(() => {
    if (!state.selectedChatId) {
      return;
    }

    const entry = state.messagesByChatId[state.selectedChatId];
    const nextPage = (entry?.page || 1) + 1;
    loadMessages(state.selectedChatId, nextPage);
  }, [loadMessages, state.messagesByChatId, state.selectedChatId]);

  const loadNewerMessages = useCallback(() => {
    if (!state.selectedChatId) {
      return;
    }

    const entry = state.messagesByChatId[state.selectedChatId];
    if (!entry?.hasNewer || entry.newestPage <= 1) {
      return;
    }

    loadMessages(state.selectedChatId, entry.newestPage - 1);
  }, [loadMessages, state.messagesByChatId, state.selectedChatId]);

  const loadLatestMessages = useCallback(async () => {
    if (!state.selectedChatId) {
      return;
    }

    const entry = state.messagesByChatId[state.selectedChatId];
    if (!entry?.hasNewer || entry.newestPage <= 1) {
      return;
    }

    for (let page = entry.newestPage - 1; page >= 1; page -= 1) {
      const didLoad = await loadMessages(state.selectedChatId, page);
      if (!didLoad) {
        break;
      }
    }
  }, [loadMessages, state.messagesByChatId, state.selectedChatId]);

  const markVisibleMessagesRead = useCallback(
    async (chat: DialogChat, messages: GlobalChatMessage[]) => {
      if (!token || !currentUserId) {
        return;
      }

      const unreadMessageIds = getExplicitlyReadableMessageIds({
        messages,
        currentUserId,
        inFlightMessageIds: readMarkInFlightRef.current,
      });

      if (unreadMessageIds.length === 0) {
        return;
      }

      unreadMessageIds.forEach((messageId) => {
        readMarkInFlightRef.current.add(messageId);
      });

      if (!isDialogChatMutedForUser(chat, currentUserId)) {
        onUnreadMessagesCountChange(
          Math.max(0, unreadMessagesCount - unreadMessageIds.length),
        );
      }

      try {
        const response = await DialogChatAPI.markMessagesAsRead(
          token,
          unreadMessageIds,
        );

        if (!response.status) {
          unreadMessageIds.forEach((messageId) => {
            readMarkInFlightRef.current.delete(messageId);
          });
          return;
        }

        dispatch({
          type: "applyMessagesRead",
          updatedMessages: unreadMessageIds.map((messageId) => ({
            _id: messageId,
            readBy: [
              ...new Set([
                ...(messages.find((item) => item.messageId === messageId)
                  ?.readBy || []),
                currentUserId,
              ]),
            ],
          })),
        });
        dispatch({
          type: "markConversationRead",
          chatId: chat._id,
        });
      } catch {
        unreadMessageIds.forEach((messageId) => {
          readMarkInFlightRef.current.delete(messageId);
        });
      }
    },
    [currentUserId, onUnreadMessagesCountChange, token, unreadMessagesCount],
  );

  const searchMessages = useCallback(
    async (query = messageSearchQuery) => {
      const normalizedQuery = query.trim();
      if (!token || !currentUserId || !selectedChat || !normalizedQuery) {
        setMessageSearchResults([]);
        setMessageSearchError("");
        return;
      }

      const requestId = ++messageSearchRequestIdRef.current;
      setMessageSearchQuery(query);
      setIsMessageSearchLoading(true);
      setMessageSearchError("");

      try {
        const response = await DialogChatAPI.searchDialogMessages(token, {
          chatId: selectedChat._id,
          query: normalizedQuery,
          page: 1,
          limit: MESSAGE_SEARCH_LIMIT,
        });

        if (requestId !== messageSearchRequestIdRef.current) {
          return;
        }

        if (response.status) {
          setMessageSearchResults(
            (response.messages || []).map((message) =>
              normalizeMessage(message, currentUserId, selectedLanguage),
            ),
          );
        } else {
          setMessageSearchResults([]);
          setMessageSearchError(strings.globalChatSearchFailed);
        }
      } catch {
        if (requestId === messageSearchRequestIdRef.current) {
          setMessageSearchResults([]);
          setMessageSearchError(strings.globalChatSearchFailed);
        }
      } finally {
        if (requestId === messageSearchRequestIdRef.current) {
          setIsMessageSearchLoading(false);
        }
      }
    },
    [currentUserId, messageSearchQuery, selectedChat, selectedLanguage, token],
  );

  const clearMessageSearch = useCallback(() => {
    messageSearchRequestIdRef.current += 1;
    setMessageSearchQuery("");
    setMessageSearchResults([]);
    setIsMessageSearchLoading(false);
    setIsJumpingToSearchResult(false);
    setMessageSearchError("");
    setFocusTargetMessageId(null);
    setHighlightedMessageId(null);
  }, []);

  const focusLoadedMessage = useCallback((messageId: string) => {
    setFocusTargetMessageId(messageId);
    setHighlightedMessageId(messageId);

    window.setTimeout(() => {
      setHighlightedMessageId((current) =>
        current === messageId ? null : current,
      );
    }, JUMP_HIGHLIGHT_DURATION_MS);
  }, []);

  const openMessageById = useCallback(
    async ({
      messageId,
      onUnavailable,
      setIsOpening,
    }: {
      messageId: string;
      onUnavailable: (message: string) => void;
      setIsOpening: (isOpening: boolean) => void;
    }) => {
      if (!token || !currentUserId || !selectedChat) {
        return;
      }

      setIsOpening(true);
      onUnavailable("");

      try {
        if (selectedMessagesById.has(messageId)) {
          focusLoadedMessage(messageId);
          void markVisibleMessagesRead(selectedChat, selectedMessages);
          return;
        }

        const response = await DialogChatAPI.getDialogMessagesAroundMessage(
          token,
          {
            chatId: selectedChat._id,
            messageId,
            limit: MESSAGES_PAGE_LIMIT,
          },
        );

        if (!response.status || !response.messages?.length) {
          onUnavailable(strings.globalChatMessageUnavailable);
          return;
        }

        const normalizedMessages = response.messages
          .map((item) =>
            normalizeMessage(item, currentUserId, selectedLanguage),
          )
          .reverse();
        const targetMessageId = response.targetMessageId || messageId;

        dispatch({
          type: "replaceMessagesAroundTarget",
          chatId: selectedChat._id,
          page: response.page || 1,
          messages: normalizedMessages,
          hasMore: normalizedMessages.length === MESSAGES_PAGE_LIMIT,
          hasNewer: (response.page || 1) > 1,
        });
        focusLoadedMessage(targetMessageId);

        void markVisibleMessagesRead(selectedChat, normalizedMessages);
      } catch {
        onUnavailable(strings.globalChatMessageUnavailable);
      } finally {
        setIsOpening(false);
      }
    },
    [
      currentUserId,
      focusLoadedMessage,
      markVisibleMessagesRead,
      selectedChat,
      selectedLanguage,
      selectedMessages,
      selectedMessagesById,
      token,
    ],
  );

  const openSearchResult = useCallback(
    async (message: GlobalChatMessage) => {
      if (message.isLocalOnly) {
        return;
      }

      await openMessageById({
        messageId: message.messageId,
        onUnavailable: setMessageSearchError,
        setIsOpening: setIsJumpingToSearchResult,
      });
    },
    [openMessageById],
  );

  const openPinnedMessage = useCallback(
    async (messageId: string) => {
      await openMessageById({
        messageId,
        onUnavailable: setPinnedMessageError,
        setIsOpening: setIsJumpingToPinnedMessage,
      });
    },
    [openMessageById],
  );

  const cancelEditingMessage = useCallback(() => {
    setEditingMessage(null);
    setComposerText("");
    setSelectedMentionIds([]);
    setReplyTarget(null);
    stopLocalTyping();
  }, [stopLocalTyping]);

  const submitEditingMessage = useCallback(async () => {
    if (!token || !currentUserId || !selectedChat || !editingMessage) {
      return;
    }

    const nextText = composerText.trim();
    if (!nextText || nextText === (editingMessage.text || "").trim()) {
      return;
    }

    const mentions = dedupeMentionIdsForChat(selectedMentionIds, selectedChat);

    setIsEditingMessage(true);
    try {
      const response = await DialogChatAPI.editDialogMessage(token, {
        messageId: editingMessage.messageId,
        message: nextText,
        mentions,
      });

      if (response.status && response.updatedMessage) {
        dispatch({
          type: "replaceMessage",
          messageId: editingMessage.messageId,
          message: normalizeMessage(
            response.updatedMessage,
            currentUserId,
            selectedLanguage,
          ),
        });
        setEditingMessage(null);
        setComposerText("");
        setSelectedFiles([]);
        setSelectedMentionIds([]);
        setReplyTarget(null);
        stopLocalTyping(selectedChat._id);
      }
    } finally {
      setIsEditingMessage(false);
    }
  }, [
    composerText,
    currentUserId,
    editingMessage,
    selectedChat,
    selectedLanguage,
    selectedMentionIds,
    stopLocalTyping,
    token,
  ]);

  const sendMessage = useCallback(
    async ({
      text = composerText,
      files = selectedFiles,
    }: {
      text?: string;
      files?: File[];
    } = {}) => {
      if (editingMessage) {
        await submitEditingMessage();
        return;
      }

      if (
        !token ||
        !currentUserId ||
        !selectedChat ||
        !canSendStructuredTextMessage({
          text,
          filesCount: files.length,
          selectedMentions: selectedMentionCandidates,
        })
      ) {
        return;
      }

      const chat = selectedChat;
      const normalizedText = text.trim();
      const mentions = dedupeMentionIdsForChat(selectedMentionIds, chat);
      const validReplyTarget = canReplyToGlobalChatMessage(replyTarget)
        ? replyTarget
        : null;
      const replyTo = validReplyTarget?.messageId;
      const replyToMessage = validReplyTarget
        ? createReplyPreviewFromMessage(validReplyTarget)
        : undefined;
      const optimisticMessage = createOptimisticMessage({
        chatId: chat._id,
        text: normalizedText,
        files: Array.from(files),
        deliveryState: "pending",
        mentions,
        replyTo,
        replyToMessage,
      });

      setComposerText("");
      setSelectedFiles([]);
      setSelectedMentionIds([]);
      setReplyTarget(null);
      stopLocalTyping(chat._id);

      dispatch({
        type: "appendMessage",
        chatId: chat._id,
        message: optimisticMessage,
        currentUserId,
      });

      await sendLocalMessage({
        chat,
        localMessageId: optimisticMessage.messageId,
        text: normalizedText,
        files: Array.from(files),
        mentions,
        replyTo,
      });
    },
    [
      composerText,
      createOptimisticMessage,
      currentUserId,
      selectedChat,
      selectedFiles,
      selectedMentionCandidates,
      selectedMentionIds,
      sendLocalMessage,
      stopLocalTyping,
      editingMessage,
      submitEditingMessage,
      token,
      replyTarget,
    ],
  );

  const sendVoiceMessage = useCallback(
    async (draft: DialogChatVoiceDraft) => {
      if (!token || !currentUserId || !selectedChat) {
        URL.revokeObjectURL(draft.url);
        return;
      }

      const chat = selectedChat;
      const optimisticMessage = createOptimisticVoiceMessage({
        chatId: chat._id,
        draft,
        deliveryState: "pending",
      });

      dispatch({
        type: "appendMessage",
        chatId: chat._id,
        message: optimisticMessage,
        currentUserId,
      });

      await sendLocalVoiceMessage({
        chat,
        localMessageId: optimisticMessage.messageId,
        draft,
      });
    },
    [
      createOptimisticVoiceMessage,
      currentUserId,
      selectedChat,
      sendLocalVoiceMessage,
      token,
    ],
  );

  const retryLocalMessage = useCallback(
    async (message: GlobalChatMessage) => {
      if (!selectedChat || !message.isLocalOnly || !message.retryPayload) {
        return;
      }

      dispatch({
        type: "updateLocalMessageDeliveryState",
        chatId: selectedChat._id,
        messageId: message.messageId,
        deliveryState: "retrying",
      });

      if (
        message.retryPayload.kind === "voice" &&
        message.retryPayload.voiceDraft
      ) {
        await sendLocalVoiceMessage({
          chat: selectedChat,
          localMessageId: message.messageId,
          draft: {
            id: message.messageId,
            ...message.retryPayload.voiceDraft,
          },
        });
        return;
      }

      await sendLocalMessage({
        chat: selectedChat,
        localMessageId: message.messageId,
        text: message.retryPayload.text,
        files: message.retryPayload.files,
        mentions: message.retryPayload.mentions || [],
        replyTo: message.retryPayload.replyTo,
      });
    },
    [selectedChat, sendLocalMessage, sendLocalVoiceMessage],
  );

  const discardLocalMessage = useCallback(
    (message: GlobalChatMessage) => {
      if (!selectedChat || !message.isLocalOnly) {
        return;
      }

      revokeLocalFilePreviewUrls(message.messageId);
      revokeLocalVoicePreviewUrl(message.messageId);
      dispatch({
        type: "discardLocalMessage",
        chatId: selectedChat._id,
        messageId: message.messageId,
      });
    },
    [revokeLocalFilePreviewUrls, revokeLocalVoicePreviewUrl, selectedChat],
  );

  const copyMessageText = useCallback(async (message: GlobalChatMessage) => {
    const text = getVisibleMessageText(message);
    if (!text) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }, []);

  const startEditingMessage = useCallback(
    (message: GlobalChatMessage) => {
      setEditingMessage(message);
      setComposerText(message.text || "");
      setSelectedFiles([]);
      setReplyTarget(null);
      setSelectedMentionIds(
        dedupeMentionIdsForChat(
          (message.mentions || []).map((mention) =>
            typeof mention === "string" ? mention : mention._id,
          ),
          selectedChat,
        ),
      );
      stopLocalTyping();
    },
    [selectedChat, stopLocalTyping],
  );

  const requestDeleteMessage = useCallback((message: GlobalChatMessage) => {
    setPendingDeleteMessage(message);
  }, []);

  const cancelDeleteMessage = useCallback(() => {
    if (!isDeletingMessage) {
      setPendingDeleteMessage(null);
    }
  }, [isDeletingMessage]);

  const confirmDeleteMessage = useCallback(async () => {
    if (!token || !pendingDeleteMessage) {
      return;
    }

    setIsDeletingMessage(true);
    try {
      const response = await DialogChatAPI.deleteDialogMessage(
        token,
        pendingDeleteMessage.messageId,
      );

      if (response.status) {
        dispatch({
          type: "deleteMessageEverywhere",
          messageId: response.messageId || pendingDeleteMessage.messageId,
        });
        if (editingMessage?.messageId === pendingDeleteMessage.messageId) {
          cancelEditingMessage();
        }
        setPendingDeleteMessage(null);
      }
    } finally {
      setIsDeletingMessage(false);
    }
  }, [
    cancelEditingMessage,
    editingMessage?.messageId,
    pendingDeleteMessage,
    token,
  ]);

  const pinMessage = useCallback(
    async (message: GlobalChatMessage) => {
      if (!token || !selectedChat) {
        return;
      }

      const response = await DialogChatAPI.pinDialogMessage(
        token,
        message.messageId,
      );

      if (response.status) {
        dispatch({
          type: "applyPinnedMessage",
          chatId: selectedChat._id,
          pinnedMessage: {
            message: response.pinnedMessage?.message || message.messageId,
            pinnedBy: response.pinnedMessage?.pinnedBy || currentUserId || "",
            pinnedAt:
              response.pinnedMessage?.pinnedAt || new Date().toISOString(),
          },
        });
      }
    },
    [currentUserId, selectedChat, token],
  );

  const unpinMessage = useCallback(
    async (message: GlobalChatMessage) => {
      if (!token || !selectedChat) {
        return;
      }

      const response = await DialogChatAPI.unpinDialogMessage(
        token,
        message.messageId,
      );

      if (response.status) {
        dispatch({
          type: "removePinnedMessage",
          chatId: selectedChat._id,
          messageId: message.messageId,
        });
      }
    },
    [selectedChat, token],
  );

  const unpinPinnedMessage = useCallback(
    async (messageId: string) => {
      if (!token || !selectedChat) {
        return false;
      }

      const response = await DialogChatAPI.unpinDialogMessage(token, messageId);

      if (response.status) {
        dispatch({
          type: "removePinnedMessage",
          chatId: selectedChat._id,
          messageId,
        });
        return true;
      }

      return false;
    },
    [selectedChat, token],
  );

  const unpinAllPinnedMessages = useCallback(async () => {
    if (!token || !selectedChat?.pinnedMessages?.length) {
      return false;
    }

    const chatId = selectedChat._id;
    const messageIds = selectedChat.pinnedMessages.map((pin) =>
      getPinnedMessageId(pin),
    );

    const results = await Promise.all(
      messageIds.map(async (messageId) => {
        const response = await DialogChatAPI.unpinDialogMessage(
          token,
          messageId,
        );

        if (response.status) {
          dispatch({
            type: "removePinnedMessage",
            chatId,
            messageId,
          });
          return true;
        }

        return false;
      }),
    );

    return results.every(Boolean);
  }, [selectedChat, token]);

  const hydratePinnedMessage = useCallback(
    async (chatId: string, pinnedMessage: PinnedDialogMessage) => {
      if (!token || !currentUserId) {
        return;
      }

      const messageId = getPinnedMessageId(pinnedMessage);
      if (selectedMessagesById.has(messageId)) {
        return;
      }

      try {
        const response = await DialogChatAPI.getDialogMessageById(
          token,
          messageId,
        );

        if (response.status && response.responseMessage) {
          dispatch({
            type: "applyPinnedMessage",
            chatId,
            pinnedMessage: {
              ...pinnedMessage,
              message: response.responseMessage,
            },
          });
        }
      } catch {
        // Thin pin payload remains usable for jump even if preview hydration fails.
      }
    },
    [currentUserId, selectedMessagesById, token],
  );

  const addMention = useCallback(
    (mention: GlobalChatMentionCandidate) => {
      if (!mentionCandidates.some((candidate) => candidate.id === mention.id)) {
        return;
      }

      setSelectedMentionIds((prev) =>
        prev.includes(mention.id) ? prev : [...prev, mention.id],
      );
      handleComposerTextChange(insertMentionText(composerText, mention.label));
    },
    [composerText, handleComposerTextChange, mentionCandidates],
  );

  const selectReplyTarget = useCallback((message: GlobalChatMessage) => {
    if (!canReplyToGlobalChatMessage(message)) {
      return;
    }

    setReplyTarget(message);
  }, []);

  const refreshVoicePlaybackUrl = useCallback(
    async (message: GlobalChatMessage) => {
      if (!token || message.isLocalOnly || !message.voice) {
        return;
      }

      const voice = await refreshDialogChatVoicePlaybackUrl({
        api: DialogChatAPI,
        token,
        messageId: message.messageId,
      });

      if (!voice) {
        return;
      }

      dispatch({
        type: "updateMessage",
        messageId: message.messageId,
        changes: { voice },
      });
    },
    [token],
  );

  const reactOnMessage = useCallback(
    async (emoji: string, messageId: string) => {
      if (!currentUserId || !currentUser) {
        return;
      }

      socketAdapter.reactToMessage({
        userId: currentUserId,
        messageId,
        emoji,
      });

      dispatch({
        type: "applyMessageReactions",
        messageId,
        emoji,
        reactBy: {
          _id: currentUserId,
          name: currentUser.name,
          surname: currentUser.surname,
        },
      });
    },
    [currentUser, currentUserId, socketAdapter],
  );

  const updateConversation = useCallback(
    (chatId: string, changes: Partial<DialogChat>) => {
      dispatch({
        type: "updateConversation",
        chatId,
        changes,
      });
    },
    [],
  );

  const updateMessage = useCallback(
    (messageId: string, changes: Partial<GlobalChatMessage>) => {
      dispatch({
        type: "updateMessage",
        messageId,
        changes,
      });
    },
    [],
  );

  const clearFocusedMessageTarget = useCallback(() => {
    setFocusTargetMessageId(null);
  }, []);

  const removeConversation = useCallback((chatId: string) => {
    dispatch({
      type: "removeConversation",
      chatId,
    });
  }, []);

  const upsertConversation = useCallback(
    (conversation: DialogChat, position: "start" | "end" = "start") => {
      dispatch({
        type: "upsertConversation",
        conversation,
        position,
      });
    },
    [],
  );

  const pinConversation = useCallback(
    async (chat: DialogChat) => {
      if (!token || !currentUserId) {
        return;
      }

      const response = await DialogChatAPI.pinDialogChat(token, chat._id);
      if (response.status) {
        updateConversation(chat._id, {
          pinnedBy: setUserIdInList(chat.pinnedBy, currentUserId, true),
        });
      }
    },
    [currentUserId, token, updateConversation],
  );

  const unpinConversation = useCallback(
    async (chat: DialogChat) => {
      if (!token || !currentUserId) {
        return;
      }

      const response = await DialogChatAPI.unpinDialogChat(token, chat._id);
      if (response.status) {
        updateConversation(chat._id, {
          pinnedBy: setUserIdInList(chat.pinnedBy, currentUserId, false),
        });
      }
    },
    [currentUserId, token, updateConversation],
  );

  const toggleMuteConversation = useCallback(
    async (chat: DialogChat) => {
      if (!token || !currentUserId) {
        return;
      }

      const response = await DialogChatAPI.muteChat(token, chat._id);
      if (response.status) {
        updateConversation(chat._id, {
          mutedBy: setUserIdInList(
            chat.mutedBy,
            currentUserId,
            !(chat.mutedBy || []).includes(currentUserId),
          ),
        });
      }
    },
    [currentUserId, token, updateConversation],
  );

  const toggleBlockConversation = useCallback(
    async (chat: DialogChat) => {
      if (!token || !currentUserId) {
        return;
      }

      const response = await DialogChatAPI.blockDialogChat(token, chat._id);
      if (response.status) {
        updateConversation(chat._id, {
          blockedBy: setUserIdInList(
            chat.blockedBy,
            currentUserId,
            !(chat.blockedBy || []).includes(currentUserId),
          ),
        });
      }
    },
    [currentUserId, token, updateConversation],
  );

  const leaveConversation = useCallback(
    async (chat: DialogChat) => {
      if (!token) {
        return;
      }

      const response = await DialogChatAPI.leaveChat(token, chat._id);
      if (response.status) {
        removeConversation(chat._id);
      }
    },
    [removeConversation, token],
  );

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    socketAdapter.joinUserDialogChannel(currentUserId);

    const unsubscribeMessageReceived = socketAdapter.onMessageReceived(
      (event) => {
        const chatId = event.newMessage.chatId || event.newMessage.dialogChat;
        if (!chatId) {
          return;
        }

        dispatch({
          type: "appendMessage",
          chatId,
          message: normalizeSocketMessage(
            event,
            currentUserId,
            selectedLanguage,
          ),
          currentUserId,
        });
      },
    );
    const unsubscribeMessageReacted = socketAdapter.onMessageReacted(
      (event) => {
        dispatch({
          type: "applyMessageReactions",
          messageId: event.messageId,
          emoji: event.emoji,
          reactBy: event.reactBy,
          reactionAdded: event.reactionAdded,
        });
      },
    );
    const unsubscribeMessagesRead = socketAdapter.onMessagesRead(
      ({ updatedMessages }) => {
        dispatch({
          type: "applyMessagesRead",
          updatedMessages,
        });
      },
    );
    const unsubscribeMessageEdited = socketAdapter.onMessageEdited((event) => {
      dispatch({
        type: "replaceMessage",
        messageId: event.messageId,
        message: normalizeSocketMessage(
          { newMessage: event.updatedMessage },
          currentUserId,
          selectedLanguage,
        ),
      });
    });
    const unsubscribeMessageDeleted = socketAdapter.onMessageDeleted(
      (event) => {
        dispatch({
          type: "deleteMessageEverywhere",
          messageId: event.messageId,
        });
      },
    );
    const unsubscribeMessagePinned = socketAdapter.onMessagePinned((event) => {
      const pinnedMessage: PinnedDialogMessage = {
        message: event.pinnedMessage.message,
        pinnedBy: event.pinnedMessage.pinnedBy,
        pinnedAt: event.pinnedMessage.pinnedAt,
      };

      dispatch({
        type: "applyPinnedMessage",
        chatId: event.chatId,
        pinnedMessage,
      });
      void hydratePinnedMessage(event.chatId, pinnedMessage);
    });
    const unsubscribeMessageUnpinned = socketAdapter.onMessageUnpinned(
      (event) => {
        dispatch({
          type: "removePinnedMessage",
          chatId: event.chatId,
          messageId: event.messageId,
        });
      },
    );
    const clearRemoteTypingUser = (chatId: string, userId: string) => {
      const timeoutKey = `${chatId}:${userId}`;
      const timeoutId = remoteTypingTimeoutsRef.current.get(timeoutKey);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        remoteTypingTimeoutsRef.current.delete(timeoutKey);
      }

      setTypingUserIdsByChatId((prev) => {
        const nextUserIds = (prev[chatId] || []).filter((id) => id !== userId);
        return {
          ...prev,
          [chatId]: nextUserIds,
        };
      });
    };
    const unsubscribeTypingStarted = socketAdapter.onTypingStarted((event) => {
      if (event.userId === currentUserId) {
        return;
      }

      setTypingUserIdsByChatId((prev) => ({
        ...prev,
        [event.chatId]: Array.from(
          new Set([...(prev[event.chatId] || []), event.userId]),
        ),
      }));

      const timeoutKey = `${event.chatId}:${event.userId}`;
      const existingTimeoutId = remoteTypingTimeoutsRef.current.get(timeoutKey);
      if (existingTimeoutId !== undefined) {
        window.clearTimeout(existingTimeoutId);
      }
      remoteTypingTimeoutsRef.current.set(
        timeoutKey,
        window.setTimeout(() => {
          clearRemoteTypingUser(event.chatId, event.userId);
        }, GLOBAL_CHAT_REMOTE_TYPING_TIMEOUT_MS),
      );
    });
    const unsubscribeTypingStopped = socketAdapter.onTypingStopped((event) => {
      clearRemoteTypingUser(event.chatId, event.userId);
    });

    return () => {
      unsubscribeMessageReceived();
      unsubscribeMessageReacted();
      unsubscribeMessagesRead();
      unsubscribeMessageEdited();
      unsubscribeMessageDeleted();
      unsubscribeMessagePinned();
      unsubscribeMessageUnpinned();
      unsubscribeTypingStarted();
      unsubscribeTypingStopped();
      remoteTypingTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      remoteTypingTimeoutsRef.current.clear();
    };
  }, [currentUserId, hydratePinnedMessage, selectedLanguage, socketAdapter]);

  useEffect(() => {
    if (
      !selectedChat?._id ||
      selectedMessagesLoading ||
      selectedMessages.length === 0
    ) {
      return;
    }

    const unreadMessageIds = getExplicitlyReadableMessageIds({
      messages: selectedMessages,
      currentUserId,
      inFlightMessageIds: readMarkInFlightRef.current,
    });

    if (unreadMessageIds.length === 0) {
      return;
    }

    void markVisibleMessagesRead(selectedChat, selectedMessages);
  }, [
    currentUserId,
    markVisibleMessagesRead,
    selectedChat,
    selectedMessages,
    selectedMessagesLoading,
  ]);

  useEffect(() => {
    if (!selectedChat?._id) {
      return;
    }

    const entry = state.messagesByChatId[selectedChat._id];
    if (!entry?.initialPageRequested) {
      loadMessages(selectedChat._id, 1);
    }
  }, [loadMessages, selectedChat?._id, state.messagesByChatId]);

  useEffect(() => {
    setSelectedMentionIds([]);
    setReplyTarget(null);
    clearMessageSearch();
  }, [clearMessageSearch, selectedChat?._id]);

  useEffect(() => {
    if (!token || !currentUserId) {
      return;
    }

    const intent = resolveGlobalChatIntent({
      searchParams,
      routeState,
      directUserId,
      currentUserId,
    });

    if (intent.type === "none") {
      return;
    }

    const intentKey = getGlobalChatIntentKey(intent);
    if (handledRouteIntentKey === intentKey) {
      return;
    }

    const markIntentHandled = () => {
      setHandledRouteIntentKey(intentKey);
      if (
        intent.type === "open-or-create-direct-chat" &&
        directUserId === intent.targetUserId
      ) {
        onDirectUserIdHandled?.();
      }
    };

    const existingChat = findDialogChatForIntent(conversations, intent);
    if (existingChat) {
      selectChat(existingChat);
      markIntentHandled();
      return;
    }

    if (intent.type !== "open-or-create-direct-chat") {
      return;
    }

    DialogChatAPI.createDialogChat(token, {
      userId: currentUserId,
      userIdToSend: intent.targetUserId,
    }).then((response) => {
      if (response.status && response.chat) {
        upsertConversation(response.chat, "start");
        selectChat(response.chat);
        markIntentHandled();
      }
    });
  }, [
    conversations,
    currentUserId,
    directUserId,
    handledRouteIntentKey,
    onDirectUserIdHandled,
    routeState,
    searchParams,
    selectChat,
    token,
    upsertConversation,
  ]);

  useEffect(() => {
    return () => {
      stopLocalTyping();
      remoteTypingTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      remoteTypingTimeoutsRef.current.clear();
      localFilePreviewUrlsRef.current.forEach((urls) => {
        urls.forEach((source) => URL.revokeObjectURL(source));
      });
      localFilePreviewUrlsRef.current.clear();
      localVoicePreviewUrlsRef.current.forEach((source) => {
        URL.revokeObjectURL(source);
      });
      localVoicePreviewUrlsRef.current.clear();
      dispatch({ type: "reset" });
    };
  }, [stopLocalTyping]);

  const selectedPinnedMessageIds = useMemo(
    () =>
      new Set(
        (selectedChat?.pinnedMessages || []).map((pin) =>
          getPinnedMessageId(pin),
        ),
      ),
    [selectedChat?.pinnedMessages],
  );

  const getMessageActions = useCallback(
    (message: GlobalChatMessage): TextChatMessageAction[] => {
      const availability = getGlobalChatMessageActionAvailability({
        message,
        currentUserId,
        capabilities,
        canWrite: canWriteInSelectedChat,
        isPinned: selectedPinnedMessageIds.has(message.messageId),
      });

      return [
        availability.reply && {
          id: "reply" as const,
          label: strings.globalChatActionReply,
          onSelect: () => selectReplyTarget(message),
        },
        availability.copy && {
          id: "copy" as const,
          label: strings.globalChatActionCopy,
          onSelect: () => {
            void copyMessageText(message);
          },
        },
        availability.edit && {
          id: "edit" as const,
          label: strings.globalChatActionEdit,
          onSelect: () => {
            startEditingMessage(message);
          },
        },
        availability.delete && {
          id: "delete" as const,
          label: strings.globalChatActionDelete,
          onSelect: () => {
            requestDeleteMessage(message);
          },
        },
        availability.pin && {
          id: "pin" as const,
          label: strings.globalChatActionPin,
          onSelect: () => {
            void pinMessage(message);
          },
        },
        availability.unpin && {
          id: "unpin" as const,
          label: strings.globalChatActionUnpin,
          onSelect: () => {
            void unpinMessage(message);
          },
        },
        availability.retry && {
          id: "retry" as const,
          label: strings.globalChatActionRetry,
          onSelect: () => {
            void retryLocalMessage(message);
          },
        },
        availability.discard && {
          id: "discard" as const,
          label: strings.globalChatActionDiscard,
          onSelect: () => discardLocalMessage(message),
        },
      ].filter(Boolean) as TextChatMessageAction[];
    },
    [
      canWriteInSelectedChat,
      capabilities,
      copyMessageText,
      currentUserId,
      discardLocalMessage,
      pinMessage,
      requestDeleteMessage,
      retryLocalMessage,
      selectReplyTarget,
      selectedPinnedMessageIds,
      startEditingMessage,
      unpinMessage,
    ],
  );

  return {
    state,
    conversations,
    groupedConversations,
    selectedChat,
    selectedMessages,
    selectedMessagesById,
    selectedMessagesHasMore,
    selectedMessagesHasNewer,
    selectedMessagesLoading,
    composerText,
    selectedFiles,
    canSendComposerMessage,
    editingMessage,
    pendingDeleteMessage,
    isEditingMessage,
    isDeletingMessage,
    mentionCandidates,
    selectedMentionIds,
    replyTarget,
    messageSearchQuery,
    messageSearchResults,
    isMessageSearchLoading,
    isJumpingToSearchResult,
    isJumpingToPinnedMessage,
    messageSearchError,
    pinnedMessageError,
    selectedPinnedMessages,
    selectedTypingIndicatorText,
    focusTargetMessageId,
    highlightedMessageId,
    lastMessageByChatId: state.lastMessageByChatId,
    conversationsHasMore: state.conversationsHasMore,
    conversationsLoading: state.conversationsLoading,
    conversationsLoaded: state.conversationsLoaded,
    conversationsSearch: state.conversationsSearch,
    selectedChatBlockedBy,
    isSelectedChatBlocked,
    isSelectedChatExpired,
    actions: {
      loadConversations,
      selectChat,
      loadOlderMessages,
      loadNewerMessages,
      loadLatestMessages,
      sendMessage,
      sendVoiceMessage,
      reactOnMessage,
      setComposerText: handleComposerTextChange,
      setSelectedFiles,
      handleComposerFocus,
      handleComposerBlur,
      addMention,
      setSelectedMentionIds,
      selectReplyTarget,
      clearReplyTarget: () => setReplyTarget(null),
      cancelEditingMessage,
      confirmDeleteMessage,
      cancelDeleteMessage,
      setMessageSearchQuery,
      searchMessages,
      clearMessageSearch,
      openSearchResult,
      openPinnedMessage,
      openMessageById: (messageId: string) =>
        openMessageById({
          messageId,
          onUnavailable: setMessageSearchError,
          setIsOpening: setIsJumpingToSearchResult,
        }),
      setConversationsSearch: (search: string) =>
        dispatch({
          type: "setConversationsSearch",
          search,
        }),
      updateConversation,
      updateMessage,
      removeConversation,
      upsertConversation,
      pinConversation,
      unpinConversation,
      unpinPinnedMessage,
      unpinAllPinnedMessages,
      toggleMuteConversation,
      toggleBlockConversation,
      leaveConversation,
      retryLocalMessage,
      discardLocalMessage,
      refreshVoicePlaybackUrl,
      getMessageActions,
      clearFocusedMessageTarget,
    },
    helpers: {
      getConversationFullname: (chat: DialogChat) =>
        `${getTranslation(
          chat.members[0].name,
          selectedLanguage,
        )} ${getTranslation(chat.members[0].surname, selectedLanguage)}`,
      getConversationTitle: (chat: DialogChat) =>
        chat.title ? getTranslation(chat.title, selectedLanguage) : undefined,
    },
  };
};
